import React, { useMemo } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useAppSelector } from '../../redux/hooks';
import { useGetSpatialLayersQuery } from '../../redux/api/spatialApi';
import SiteMap3D from '../map/SiteMap3D';

/** Try to parse lat/lon from various site metadata fields. */
function parseSiteCoords(site: any): [number, number] | null {
  // Try meta.site_lat_lon first (most common from extraction)
  const latLon = site?.meta?.site_lat_lon;
  if (latLon) {
    if (typeof latLon === 'string') {
      const parts = latLon.split(',').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return [parts[1], parts[0]]; // [lon, lat]
      }
    }
    if (Array.isArray(latLon) && latLon.length === 2) {
      return [Number(latLon[1]), Number(latLon[0])]; // [lon, lat]
    }
  }

  // Try location field "lat,lon"
  const location = site?.location;
  if (typeof location === 'string' && location.includes(',')) {
    const parts = location.split(',').map(Number);
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return [parts[1], parts[0]];
    }
  }

  return null;
}

/** Pin fallback when no spatial data exists. */
const PinFallback: React.FC<{ siteName: string; coords: [number, number] }> = ({
  siteName,
  coords,
}) => (
  <Map
    mapLib={maplibregl as any}
    initialViewState={{
      longitude: coords[0],
      latitude: coords[1],
      zoom: 14,
      pitch: 45,
    }}
    style={{ width: '100%', height: '100%' }}
    mapStyle={{
      version: 8,
      sources: {
        'esri-satellite': {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          attribution: '&copy; Esri',
          maxzoom: 19,
        },
      },
      layers: [
        {
          id: 'satellite',
          type: 'raster',
          source: 'esri-satellite',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    }}
  >
    <NavigationControl position="top-left" visualizePitch />
    <Marker longitude={coords[0]} latitude={coords[1]} anchor="bottom">
      <div className="flex flex-col items-center">
        <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg mb-1 whitespace-nowrap">
          {siteName}
        </div>
        <MapPin size={32} className="text-blue-600 drop-shadow-lg" fill="currentColor" />
      </div>
    </Marker>
  </Map>
);

const SiteMapTab: React.FC = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || currentSite?.id;

  const {
    data: layersData,
    isLoading,
    isError,
  } = useGetSpatialLayersQuery(siteId!, { skip: !siteId });

  const coords = useMemo(() => parseSiteCoords(currentSite), [currentSite]);
  const siteName = currentSite?.site_name || currentSite?.name || 'Site';

  if (!currentSite || !siteId) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--color-text-secondary)]">
        <MapPin size={20} className="mr-2" />
        Select a site to view the map
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--color-text-secondary)]">
        <Loader2 size={20} className="mr-2 animate-spin" />
        Loading spatial data...
      </div>
    );
  }

  // Has spatial layers — show full 3D map
  if (layersData && layersData.layers.length > 0 && layersData.bbox && layersData.center) {
    return (
      <SiteMap3D
        siteId={siteId}
        layers={layersData.layers}
        bbox={layersData.bbox as [number, number, number, number]}
        center={layersData.center as [number, number]}
      />
    );
  }

  // Pin fallback
  if (coords) {
    return (
      <div className="relative w-full h-full">
        <PinFallback siteName={siteName} coords={coords} />
        <div className="absolute bottom-3 left-3 bg-[var(--color-surface)]/90 text-[var(--color-text-secondary)] text-xs px-3 py-2 rounded-lg border border-[var(--color-border)]">
          No spatial data uploaded. Showing site pin location.
        </div>
      </div>
    );
  }

  // No coords at all
  return (
    <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-secondary)] gap-2">
      <MapPin size={32} className="text-[var(--color-text-tertiary)]" />
      <span className="text-sm">No geographic data available for {siteName}</span>
      <span className="text-xs text-[var(--color-text-tertiary)]">
        Upload a GeoJSON file or add site coordinates
      </span>
    </div>
  );
};

export default SiteMapTab;
